import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { userAPI } from '../services/api';
import GlobalDashboard from '../components/GlobalDashboard';
import { useTranslation } from 'react-i18next';
import { cacheManager } from '../utils/cacheManager';
import { getSocketManager } from '../utils/socketManager';
import { CardBox, Button, Badge } from '../components/UI';
import { IconHistory, IconRefresh, IconAlertCircle } from '@tabler/icons-react';

const AccountOverview = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const fetchDashboardData = async () => {
      const cacheKey = `overview_dashboard_${user?.id}`;

      try {
        // Check for cached data first
        const cachedData = cacheManager.get(cacheKey);

        if (cachedData) {
          // Display cached data immediately
          setOverviewData(cachedData);
          setLoading(false);

          // Check if backend data is cached (has _cached property)
          if (!cachedData._cached) {
            // If client-side cached but not server-side cached,
            // refresh in background
            setIsRefreshing(true);

            try {
              const freshData = await userAPI.getOverviewDashboard();
              setOverviewData(freshData);
              cacheManager.set(cacheKey, freshData);
              setIsRefreshing(false);
            } catch (refreshError) {
              console.warn('Failed to refresh dashboard data:', refreshError);
              setIsRefreshing(false);
              // Keep showing cached data
            }
          }
        } else {
          // No cached data, fetch from API
          setLoading(true);
          const overviewDashboard = await userAPI.getOverviewDashboard();
          setOverviewData(overviewDashboard);

          // Cache the fetched data
          cacheManager.set(cacheKey, overviewDashboard);

          setLoading(false);
        }

      } catch (error) {
        console.error('Failed to fetch overview dashboard data:', error);

        // Try to use cached data as fallback
        const cachedData = cacheManager.get(cacheKey);
        if (cachedData) {
          setOverviewData(cachedData);
          setError('Using cached data - unable to fetch latest');
        } else {
          setError(error.message);
        }
        setLoading(false);
      }
    };

    if (user?.id) {
      fetchDashboardData();
    }
  }, [user?.id]);

  // Listen for batch completion events to invalidate cache
  useEffect(() => {
    if (!user?.id) return;

    const socketManager = getSocketManager();

    const handleBatchCompleted = () => {
      // Invalidate client-side cache when a batch completes
      const cacheKey = `overview_dashboard_${user.id}`;
      cacheManager.remove(cacheKey);

      // Refresh data automatically
      setIsRefreshing(true);
      userAPI.getOverviewDashboard()
        .then(freshData => {
          setOverviewData(freshData);
          cacheManager.set(cacheKey, freshData);
          setIsRefreshing(false);
        })
        .catch(error => {
          console.warn('Failed to refresh after batch completion:', error);
          setIsRefreshing(false);
        });
    };

    // Connect socket if not already connected
    if (!socketManager.isConnected()) {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      const socketUrl = apiUrl.replace('/api', '');
      socketManager.connect(socketUrl);
    }

    // Join user room
    socketManager.emit('join-user', user.id);

    // Listen for batch completion events
    const unsubscribe = socketManager.on('batch-completed', handleBatchCompleted);

    return () => {
      unsubscribe();
      socketManager.emit('leave-user', user.id);
    };
  }, [user?.id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: '60vh' }}>
        <CardBox className="text-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-500 dark:text-[#999999]">
            {t('dashboard.overview.loading') || 'Loading dashboard...'}
          </p>
        </CardBox>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: '60vh' }}>
        <CardBox className="max-w-lg">
          <div className="flex items-start gap-4">
            <IconAlertCircle size={24} className="text-red-500 mt-1" />
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-[#f5f5f5] mb-2">
                {t('dashboard.overview.loadError') || 'Failed to load dashboard'}
              </h3>
              <p className="text-sm text-gray-500 dark:text-[#999999]">{error}</p>
            </div>
          </div>
        </CardBox>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <CardBox>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="dashboard-page-title text-gray-900 dark:text-[#f5f5f5]">
              {t('dashboard.overview.welcomeBack') || 'Welcome back'}, {user?.firstName}
            </h1>
            <p className="text-gray-500 dark:text-[#999999] mt-1">
              {t('dashboard.overview.subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-4">
            {isRefreshing && (
              <div className="flex items-center gap-2">
                <IconRefresh size={18} className="animate-spin text-blue-500" />
                <span className="text-sm text-blue-500">
                  {t('dashboard.overview.refreshing') || 'Refreshing...'}
                </span>
              </div>
            )}
            {overviewData?._cached && (
              <Badge color="default">
                {t('dashboard.overview.cachedData') || 'Cached'} • {overviewData._cacheTime && new Date(overviewData._cacheTime).toLocaleTimeString()}
              </Badge>
            )}
            <Button
              onClick={() => navigate('/dashboard/history')}
              leftIcon={<IconHistory size={18} />}
            >
              {t('dashboard.history.viewHistory') || 'View History'}
            </Button>
          </div>
        </div>
      </CardBox>

      {/* Global Analytics Dashboard */}
      <GlobalDashboard overviewData={overviewData} viewMode="simple" />
    </div>
  );
};

export default AccountOverview;